import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  API, Card, DataTable, Empty, Spin, useApi, authHeaders, fmtWhen, CustomerLink,
} from './SecurityShared';
import { Plus, Trash2, Bell, CheckCheck, RefreshCw } from 'lucide-react';

/** Alerts feed — everything risk_guard e-mails to the operator. */
export function AlertsTab({ token, onOpenCustomer }) {
  const [subject, setSubject] = useState('');
  const [unseenOnly, setUnseenOnly] = useState(false);
  const qs = `/api/admin/security/alerts?limit=100${subject ? `&subject=${encodeURIComponent(subject)}` : ''}${unseenOnly ? '&seen=false' : ''}`;
  const { data, loading, reload } = useApi(token, qs, [subject, unseenOnly]);

  const markSeen = async (id, all = false) => {
    const url = all ? '/api/admin/security/alerts/seen-all' : `/api/admin/security/alerts/${id}/seen`;
    const r = await fetch(`${API}${url}`, { method: 'POST', headers: authHeaders(token) });
    if (!r.ok) { toast.error('Failed'); return; }
    toast.success(all ? 'All alerts marked as seen' : 'Marked as seen');
    reload();
  };

  return (
    <Card
      title="Alerts"
      hint="Every event that also goes out by e-mail: large top-up, balance cap, bonus, order bursts, originals from non-trusted clients, 4xx/410 spikes."
      testid="sec-alerts-card"
      right={(
        <>
          <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="sec-alerts-subject"
            className="px-2 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white max-w-[200px]">
            <option value="">All types</option>
            {(data?.subjects || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-1 text-[11px] font-semibold text-slate-600">
            <input type="checkbox" checked={unseenOnly} onChange={(e) => setUnseenOnly(e.target.checked)} data-testid="sec-alerts-unseen" />
            Unseen
          </label>
          <button onClick={() => markSeen(null, true)} data-testid="sec-alerts-seen-all"
            className="px-2 py-1.5 text-xs font-bold rounded-lg bg-slate-800 text-white flex items-center gap-1">
            <CheckCheck className="w-3.5 h-3.5" /> Seen
          </button>
          <button onClick={reload} data-testid="sec-alerts-refresh" className="p-1.5 rounded-lg border border-slate-300">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </>
      )}
    >
      {loading ? <Spin /> : !data?.rows?.length ? <Empty testid="sec-alerts-empty">No alerts in this view</Empty> : (
        <div className="space-y-2" data-testid="sec-alerts-list">
          {data.rows.map((a, i) => (
            <div key={a.id || i} data-testid={`sec-alert-${i}`}
              className={`p-3 rounded-lg border ${a.seen ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Bell className={`w-4 h-4 ${a.seen ? 'text-slate-400' : 'text-amber-600'}`} />
                <span className="font-bold text-sm text-slate-800">{a.subject}</span>
                <span className="text-[11px] text-slate-500">{fmtWhen(a.created_at)}</span>
                {a.customer_email && (
                  <CustomerLink email={a.customer_email} userId={a.customer_id}
                    onOpenCustomer={onOpenCustomer} testid={`sec-alert-customer-${i}`} />
                )}
                {!a.seen && a.id && (
                  <button onClick={() => markSeen(a.id)} data-testid={`sec-alert-seen-${i}`}
                    className="ml-auto text-[11px] font-bold text-slate-600 hover:text-slate-900">Mark seen</button>
                )}
              </div>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-0.5">
                {Object.entries(a.rows || {}).map(([k, v]) => (
                  <p key={k} className="text-[11px] text-slate-600 truncate">
                    <span className="text-slate-400">{k}: </span><b>{String(v)}</b>
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** Append-only admin audit trail. No delete button anywhere by design. */
export function AuditTab({ token, deletionsOnly = false }) {
  const [ev, setEv] = useState('');
  const [actor, setActor] = useState('');
  const [target, setTarget] = useState('');
  const path = deletionsOnly
    ? '/api/admin/security/deletions?limit=200'
    : `/api/admin/security/audit?limit=200${ev ? `&event_prefix=${encodeURIComponent(ev)}` : ''}${actor ? `&actor=${encodeURIComponent(actor)}` : ''}${target ? `&target=${encodeURIComponent(target)}` : ''}`;
  const { data, loading, reload } = useApi(token, path, [ev, actor, target, deletionsOnly]);

  return (
    <Card
      title={deletionsOnly ? 'Deleted' : 'Action log'}
      hint={deletionsOnly
        ? 'Deletion is permanent on this platform — this is the paper trail of who deleted what and when.'
        : 'Read-only. Roles, balances, Trusted, recharge decisions, deletions, limits and blocklist changes.'}
      testid={deletionsOnly ? 'sec-deleted-card' : 'sec-audit-card'}
      right={!deletionsOnly && (
        <>
          <select value={ev} onChange={(e) => setEv(e.target.value)} data-testid="sec-audit-event"
            className="px-2 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white max-w-[190px]">
            <option value="">All events</option>
            {(data?.events || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Admin e-mail"
            data-testid="sec-audit-actor" className="px-2 py-1.5 text-xs rounded-lg border border-slate-300 w-32" />
          <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Customer"
            data-testid="sec-audit-target" className="px-2 py-1.5 text-xs rounded-lg border border-slate-300 w-32" />
          <button onClick={reload} className="p-1.5 rounded-lg border border-slate-300" data-testid="sec-audit-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </>
      )}
    >
      {loading ? <Spin /> : !data?.rows?.length ? <Empty testid="sec-audit-empty">No records</Empty> : (
        <DataTable testid="sec-audit-table" rows={data.rows}
          rowKey={(r, i) => r.id || i} rowTestid={(r, i) => `sec-audit-row-${i}`}
          cols={[
            { key: 'event', label: 'Event', primary: true,
              tdClass: 'font-mono font-semibold text-slate-800',
              render: (r) => <span className="font-mono font-semibold">{r.event}</span> },
            { key: 'result', label: 'Result', primary: true,
              render: (r) => (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                  r.result === 'ok' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{r.result || '—'}</span>) },
            { key: 'when', label: 'When', tdClass: 'whitespace-nowrap text-slate-500', render: (r) => fmtWhen(r.ts) },
            { key: 'actor', label: 'Admin', tdClass: 'text-slate-700', render: (r) => r.actor_email || '—' },
            { key: 'target', label: 'Customer', tdClass: 'text-slate-700', render: (r) => r.target_email || r.target_user_id || '—' },
            { key: 'meta', label: 'Details', full: true, tdClass: 'text-slate-500 max-w-[320px] truncate',
              render: (r) => (
                <span title={JSON.stringify(r.meta || {})}>
                  {r.reason ? `${r.reason} · ` : ''}{r.meta ? JSON.stringify(r.meta) : '—'}
                </span>) },
          ]} />
      )}
    </Card>
  );
}

/** Blocklist — e-mail, mail domain, IP, phone. A hit blocks signup AND login. */
export function BlocklistTab({ token }) {
  const [kind, setKind] = useState('email');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [filter, setFilter] = useState('');
  const { data, loading, reload } = useApi(token, `/api/admin/security/blocklist${filter ? `?type=${filter}` : ''}`, [filter]);

  const add = async () => {
    if (!value.trim()) { toast.error('Enter a value'); return; }
    const r = await fetch(`${API}/api/admin/security/blocklist`, {
      method: 'POST', headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: kind, value: value.trim(), reason: reason.trim() }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.detail || 'Failed'); return; }
    toast.success(`${kind} blocked`); setValue(''); setReason(''); reload();
  };

  const remove = async (row) => {
    if (!window.confirm(`Unblock ${row.type} ${row.value}?`)) return;
    const r = await fetch(`${API}/api/admin/security/blocklist?type=${row.type}&value=${encodeURIComponent(row.value)}`,
      { method: 'DELETE', headers: authHeaders(token) });
    if (!r.ok) { toast.error('Failed'); return; }
    toast.success('Unblocked'); reload();
  };

  return (
    <Card title="Blocklist" testid="sec-blocklist-card"
      hint="Registration AND login are refused on a match. Throw-away mail providers are blocked separately by the built-in list.">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="sec-bl-type"
          className="px-2 py-2 text-sm rounded-lg border border-slate-300 bg-white">
          {(data?.types || ['email', 'domain', 'ip', 'phone']).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="sec-bl-value"
          placeholder={kind === 'email' ? 'name@example.com' : kind === 'domain' ? 'example.com' : kind === 'ip' ? '1.2.3.4' : '+49 151 …'}
          className="px-3 py-2 text-sm rounded-lg border border-slate-300 flex-1 min-w-[180px]" />
        <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sec-bl-reason"
          placeholder="Reason" className="px-3 py-2 text-sm rounded-lg border border-slate-300 flex-1 min-w-[140px]" />
        <button onClick={add} data-testid="sec-bl-add"
          className="px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-bold flex items-center gap-1">
          <Plus className="w-4 h-4" /> Block
        </button>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="sec-bl-filter"
          className="ml-auto px-2 py-2 text-xs rounded-lg border border-slate-300 bg-white">
          <option value="">Show all</option>
          {(data?.types || []).map((t) => <option key={t} value={t}>{t} only</option>)}
        </select>
      </div>
      {loading ? <Spin /> : !data?.rows?.length ? <Empty testid="sec-bl-empty">Blocklist is empty</Empty> : (
        <DataTable testid="sec-bl-table" rows={data.rows}
          rowKey={(r) => `${r.type}-${r.value}`} rowTestid={(r, i) => `sec-bl-row-${i}`}
          cols={[
            { key: 'value', label: 'Value', primary: true,
              tdClass: 'font-mono text-slate-800 break-all',
              render: (r) => (
                <span className="font-mono break-all">
                  {r.value}{r.legacy && <span className="ml-1 text-[9px] text-slate-400">legacy</span>}
                </span>) },
            { key: 'type', label: 'Type', primary: true,
              render: (r) => <span className="px-1.5 py-0.5 rounded bg-slate-200 text-[10px] font-bold uppercase">{r.type}</span> },
            { key: 'reason', label: 'Reason', tdClass: 'text-slate-600', render: (r) => r.reason || '—' },
            { key: 'by', label: 'Added by', tdClass: 'text-slate-600', render: (r) => r.added_by || '—' },
            { key: 'when', label: 'When', tdClass: 'text-slate-500 whitespace-nowrap', render: (r) => fmtWhen(r.added_at) },
            { key: 'action', label: 'Action', right: true, full: true,
              render: (r, i) => (
                <button onClick={() => remove(r)} data-testid={`sec-bl-remove-${i}`}
                  className="px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> <span className="md:hidden">Unblock</span>
                </button>) },
          ]} />
      )}
    </Card>
  );
}
